import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchMgmtReport, fetchEventsReport, fetchStayFeedback, hasFeature, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { printTableReport } from "../utils/printDoc";
import { useT } from "../i18n/useT";

// Front Desk - Phase B4: management reports (one page, tabbed) over a date range.
const S = {
  en: {
    title: "Reports", subtitle: "Front-desk management reports by arrival date", back: "Back to board",
    from: "From", to: "To", run: "Run", bookings: "bookings",
    t_pay: "Payments", t_rev: "Revenue", t_seg: "Segments", t_disc: "Discounts", t_events: "Events",
    ev_covers: "Covers", ev_deposit: "Deposit held", ev_balance: "Balance owed", ev_type: "Type", ev_status: "Status", ev_count: "Events",
    method: "Method", count: "Count", etb: "ETB", none: "Nothing in this range.",
    room: "Room", extras: "Extras", service: "Service charge", tax: "Tax", total: "Total",
    variance: "Rate variance (rack vs charged)", market: "Market", source: "Source", revenue: "Revenue",
    upsell: "Upsell revenue", upsell_agent: "Agent", commission: "Commission",
    t_feedback: "Feedback", fb_overall: "Overall", fb_staff: "Staff", fb_positive: "Positive (4–5★)", fb_comment2: "Comment",
    guest: "Guest", amount: "Amount", reason: "Reason", date: "Date", unset: "Unset",
    print: "Print", value: "Value",
  },
  am: {
    title: "ሪፖርቶች", subtitle: "የፊት ዴስክ የአስተዳደር ሪፖርቶች", back: "ወደ ዳሽቦርድ ተመለስ",
    from: "ከ", to: "እስከ", run: "አሂድ", bookings: "ማስያዣዎች",
    t_pay: "ክፍያዎች", t_rev: "ገቢ", t_seg: "ክፍሎች", t_disc: "ቅናሾች", t_events: "ዝግጅቶች",
    ev_covers: "ሰዎች", ev_deposit: "ቅድሚያ ክፍያ", ev_balance: "ቀሪ ክፍያ", ev_type: "ዓይነት", ev_status: "ሁኔታ", ev_count: "ዝግጅቶች",
    method: "ዘዴ", count: "ብዛት", etb: "ብር", none: "በዚህ ክልል ምንም የለም።",
    room: "ክፍል", extras: "ተጨማሪ", service: "የአገልግሎት ክፍያ", tax: "ታክስ", total: "ጠቅላላ",
    variance: "የዋጋ ልዩነት", market: "ገበያ", source: "ምንጭ", revenue: "ገቢ",
    upsell: "ተጨማሪ ሽያጭ ገቢ", upsell_agent: "ወኪል", commission: "ኮሚሽን",
    t_feedback: "አስተያየት", fb_overall: "አጠቃላይ", fb_staff: "ሰራተኛ", fb_positive: "አዎንታዊ (4–5★)", fb_comment2: "አስተያየት",
    guest: "እንግዳ", amount: "መጠን", reason: "ምክንያት", date: "ቀን", unset: "ያልተገለጸ",
    print: "አትም", value: "ዋጋ",
  },
  om: {
    title: "Gabaasota", subtitle: "Gabaasota bulchiinsaa fuula duraa", back: "Gara gabatee deebi'i",
    from: "Irraa", to: "Hamma", run: "Fiigi", bookings: "booking",
    t_pay: "Kaffaltii", t_rev: "Galii", t_seg: "Kutaalee", t_disc: "Hir'ina", t_events: "Sagantaalee",
    ev_covers: "Namoota", ev_deposit: "Kaffaltii duraa", ev_balance: "Hafe", ev_type: "Gosa", ev_status: "Haala", ev_count: "Sagantaalee",
    method: "Mala", count: "Baay'ina", etb: "ETB", none: "Daangaa kana keessa homaa hin jiru.",
    room: "Kutaa", extras: "Dabalata", service: "Kaffaltii tajaajilaa", tax: "Gibira", total: "Ida'ama",
    variance: "Garaagarummaa gatii", market: "Gabaa", source: "Madda", revenue: "Galii",
    upsell: "Galii gurgurtaa dabalataa", upsell_agent: "Bakka bu'aa", commission: "Komiishinii",
    t_feedback: "Yaada", fb_overall: "Waliigala", fb_staff: "Hojjetaa", fb_positive: "Gaarii (4–5★)", fb_comment2: "Yaada",
    guest: "Keessummaa", amount: "Hanga", reason: "Sababa", date: "Guyyaa", unset: "Hin murtoofne",
    print: "Maxxansi", value: "Gatii",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const iso = (off) => { const d = new Date(); d.setDate(d.getDate() + off); return d.toISOString().slice(0, 10); };
const TH = { textAlign: "left", color: "#5b6472", padding: "3px 6px" };
const TR = { textAlign: "right" };

export default function FrontDeskReportsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const [from, setFrom] = useState(iso(-30));
  const [to, setTo] = useState(iso(0));
  const [tab, setTab] = useState("pay");
  const [d, setD] = useState(null);
  const [ev, setEv] = useState(null);
  const [fb, setFb] = useState(null);
  const fbOn = hasFeature("ratings");

  const load = () => {
    setD(null); setEv(null); setFb(null);
    fetchMgmtReport(from, to).then(setD).catch(() => setD({}));
    fetchEventsReport(from, to).then(setEv).catch(() => setEv({}));
    if (fbOn) fetchStayFeedback(from, to).then(setFb).catch(() => setFb({}));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };
  const tabBtn = (k) => ({
    padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13.5, fontWeight: tab === k ? 700 : 500,
    border: tab === k ? "1.5px solid #0F6E56" : "1px solid #e2e6ea", background: tab === k ? "#E7F6EC" : "#fff",
    color: tab === k ? "#0F6E56" : "#5b6472",
  });
  const rev = (d && d.revenue) || {};

  const doPrint = () => {
    if (!d) return;
    const m = (v) => formatPrice(v || 0, cur);
    let heading = ""; let sections = [];
    if (tab === "pay") {
      heading = t("t_pay");
      sections = [{ headers: [t("method"), t("count"), t("etb")], rows: (d.payments_by_method || []).map((p) => [p.method, p.count, m(p.etb)]) }];
    } else if (tab === "rev") {
      heading = t("t_rev");
      sections = [{ headers: [t("t_rev"), t("value")], rows: [
        [t("room"), m(rev.room)], [t("extras"), m(rev.extras)], [t("service"), m(rev.service_charge)], [t("tax"), m(rev.tax)],
        Object.assign([t("total"), m(rev.total)], { bold: true }),
        [t("variance"), m(d.rate_variance_total)], [t("upsell"), m(d.upsell_total)],
      ] }];
      if ((d.upsell_by_agent || []).length) sections.push({ heading: t("upsell_agent"), headers: [t("upsell_agent"), t("count"), t("revenue"), t("commission")], rows: d.upsell_by_agent.map((a) => [a.agent, a.count, m(a.amount), m(a.commission)]) });
    } else if (tab === "seg") {
      heading = t("t_seg");
      sections = [
        { heading: t("market"), headers: [t("market"), t("count"), t("revenue")], rows: (d.by_market || []).map((x) => [x.market === "unset" ? t("unset") : x.market, x.count, m(x.revenue)]) },
        { heading: t("source"), headers: [t("source"), t("count"), t("revenue")], rows: (d.by_source || []).map((x) => [x.source, x.count, m(x.revenue)]) },
      ];
    } else if (tab === "disc") {
      heading = t("t_disc");
      sections = [{ headers: [t("date"), t("guest"), t("reason"), t("amount")], rows: (d.discounts || []).map((x) => [x.date, x.guest_name, x.reason || "-", m(x.amount)]) }];
    } else if (tab === "events" && ev && ev.totals) {
      heading = t("t_events");
      sections = [
        { headers: [t("t_events"), t("value")], rows: [
          [t("ev_count"), ev.totals.count], [t("ev_covers"), ev.totals.covers], [t("service"), m(ev.totals.service_charge)], [t("tax"), m(ev.totals.tax_amount)],
          Object.assign([t("total"), m(ev.totals.total)], { bold: true }),
          [t("ev_deposit"), m(ev.totals.deposit)], [t("ev_balance"), m(ev.totals.balance)],
        ] },
        { heading: t("ev_type"), headers: [t("ev_type"), t("count"), t("revenue")], rows: (ev.by_type || []).map((x) => [x.type, x.count, m(x.revenue)]) },
        { heading: t("ev_status"), headers: [t("ev_status"), t("count"), t("revenue")], rows: (ev.by_status || []).map((x) => [x.status, x.count, m(x.revenue)]) },
      ];
    } else if (tab === "fb" && fb) {
      heading = t("t_feedback");
      sections = [
        { headers: [t("t_feedback"), t("value")], rows: [
          [t("fb_overall"), fb.avg_overall != null ? `${fb.avg_overall} ★` : "—"],
          [t("fb_staff"), fb.avg_staff != null ? `${fb.avg_staff} ★` : "—"],
          [t("fb_positive"), fb.positive_pct != null ? `${fb.positive_pct}%` : "—"],
          [t("count"), fb.count],
        ] },
        { heading: t("t_feedback"), headers: [t("date"), t("guest"), t("fb_overall"), t("fb_staff"), t("fb_comment2")], rows: (fb.recent || []).map((x) => [x.date ? x.date.slice(0, 10) : "", x.guest_name, `${x.overall} ★`, `${x.staff} ★`, x.comment || "-"]) },
      ];
    }
    printTableReport({ title: `${t("title")} — ${heading}`, subtitle: `${t("from")} ${from} — ${t("to")} ${to}`, sections });
  };

  return (
    <div style={{ maxWidth: 940, margin: "0 auto" }}>
      <Link className="muted" to="/front-desk/board" style={{ fontSize: 13, textDecoration: "none" }}>&larr; {t("back")}</Link>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("from")} <input type="date" style={{ ...inp, marginLeft: 4 }} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("to")} <input type="date" style={{ ...inp, marginLeft: 4 }} value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button className="btn primary small" onClick={load}>{t("run")}</button>
        {d && d.bookings != null && <span className="muted" style={{ fontSize: 13 }}>{d.bookings} {t("bookings")}</span>}
        <span style={{ flex: 1 }} />
        <button className="btn secondary small" disabled={!d} onClick={doPrint}>🖨 {t("print")}</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button style={tabBtn("pay")} onClick={() => setTab("pay")}>{t("t_pay")}</button>
        <button style={tabBtn("rev")} onClick={() => setTab("rev")}>{t("t_rev")}</button>
        <button style={tabBtn("seg")} onClick={() => setTab("seg")}>{t("t_seg")}</button>
        <button style={tabBtn("disc")} onClick={() => setTab("disc")}>{t("t_disc")}</button>
        <button style={tabBtn("events")} onClick={() => setTab("events")}>{t("t_events")}</button>
        {fbOn && <button style={tabBtn("fb")} onClick={() => setTab("fb")}>{t("t_feedback")}</button>}
      </div>

      {d == null ? <p className="muted" style={{ marginTop: 14 }}>...</p> : (
        <div style={{ ...CARD, padding: 14, marginTop: 12 }}>
          {tab === "pay" && (
            (d.payments_by_method || []).length === 0 ? <div className="muted" style={{ fontSize: 13 }}>{t("none")}</div> : (
              <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                <thead><tr><th style={TH}>{t("method")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("etb")}</th></tr></thead>
                <tbody>{d.payments_by_method.map((p) => (
                  <tr key={p.method}><td style={{ padding: "3px 6px", fontWeight: 600 }}>{p.method}</td><td style={TR}>{p.count}</td><td style={TR}>{formatPrice(p.etb, cur)}</td></tr>
                ))}</tbody>
              </table>
            )
          )}
          {tab === "rev" && (
            <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
              <tbody>
                <tr><td style={{ padding: "4px 6px" }}>{t("room")}</td><td style={TR}>{formatPrice(rev.room || 0, cur)}</td></tr>
                <tr><td style={{ padding: "4px 6px" }}>{t("extras")}</td><td style={TR}>{formatPrice(rev.extras || 0, cur)}</td></tr>
                <tr><td style={{ padding: "4px 6px" }}>{t("service")}</td><td style={TR}>{formatPrice(rev.service_charge || 0, cur)}</td></tr>
                <tr><td style={{ padding: "4px 6px" }}>{t("tax")}</td><td style={TR}>{formatPrice(rev.tax || 0, cur)}</td></tr>
                <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}><td style={{ padding: "5px 6px" }}>{t("total")}</td><td style={TR}>{formatPrice(rev.total || 0, cur)}</td></tr>
                <tr><td style={{ padding: "4px 6px", color: "#8A5A0B" }}>{t("variance")}</td><td style={{ ...TR, color: "#8A5A0B" }}>{formatPrice(d.rate_variance_total || 0, cur)}</td></tr>
                <tr><td style={{ padding: "4px 6px", color: "#0F6E56" }}>{t("upsell")}</td><td style={{ ...TR, color: "#0F6E56" }}>{formatPrice(d.upsell_total || 0, cur)}</td></tr>
              </tbody>
            </table>
          )}
          {tab === "rev" && (d.upsell_by_agent || []).length > 0 && (
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse", marginTop: 12 }}>
              <thead><tr><th style={TH}>{t("upsell_agent")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("revenue")}</th><th style={{ ...TH, ...TR }}>{t("commission")}</th></tr></thead>
              <tbody>{d.upsell_by_agent.map((a) => (
                <tr key={a.agent}><td style={{ padding: "3px 6px" }}>{a.agent}</td><td style={TR}>{a.count}</td><td style={TR}>{formatPrice(a.amount, cur)}</td><td style={TR}>{formatPrice(a.commission, cur)}</td></tr>
              ))}</tbody>
            </table>
          )}
          {tab === "seg" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("market")}</div>
                <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                  <thead><tr><th style={TH}>{t("market")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("revenue")}</th></tr></thead>
                  <tbody>{(d.by_market || []).map((x) => (
                    <tr key={x.market}><td style={{ padding: "3px 6px" }}>{x.market === "unset" ? t("unset") : x.market}</td><td style={TR}>{x.count}</td><td style={TR}>{formatPrice(x.revenue, cur)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("source")}</div>
                <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                  <thead><tr><th style={TH}>{t("source")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("revenue")}</th></tr></thead>
                  <tbody>{(d.by_source || []).map((x) => (
                    <tr key={x.source}><td style={{ padding: "3px 6px" }}>{x.source}</td><td style={TR}>{x.count}</td><td style={TR}>{formatPrice(x.revenue, cur)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "disc" && (
            (d.discounts || []).length === 0 ? <div className="muted" style={{ fontSize: 13 }}>{t("none")}</div> : (
              <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                <thead><tr><th style={TH}>{t("date")}</th><th style={TH}>{t("guest")}</th><th style={TH}>{t("reason")}</th><th style={{ ...TH, ...TR }}>{t("amount")}</th></tr></thead>
                <tbody>{d.discounts.map((x) => (
                  <tr key={x.id}><td style={{ padding: "3px 6px" }}>{x.date}</td><td>{x.guest_name}</td><td>{x.reason || "-"}</td><td style={{ ...TR, color: "#A32D2D" }}>{formatPrice(x.amount, cur)}</td></tr>
                ))}</tbody>
              </table>
            )
          )}
          {tab === "events" && (
            ev == null ? <div className="muted" style={{ fontSize: 13 }}>...</div> :
            (!ev.totals || !ev.totals.count ? <div className="muted" style={{ fontSize: 13 }}>{t("none")}</div> : (
              <div>
                <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse", marginBottom: 14 }}>
                  <tbody>
                    <tr><td style={{ padding: "3px 6px" }}>{t("ev_count")}</td><td style={TR}>{ev.totals.count}</td></tr>
                    <tr><td style={{ padding: "3px 6px" }}>{t("ev_covers")}</td><td style={TR}>{ev.totals.covers}</td></tr>
                    <tr><td style={{ padding: "3px 6px" }}>{t("service")}</td><td style={TR}>{formatPrice(ev.totals.service_charge, cur)}</td></tr>
                    <tr><td style={{ padding: "3px 6px" }}>{t("tax")}</td><td style={TR}>{formatPrice(ev.totals.tax_amount, cur)}</td></tr>
                    <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}><td style={{ padding: "4px 6px" }}>{t("total")}</td><td style={TR}>{formatPrice(ev.totals.total, cur)}</td></tr>
                    <tr><td style={{ padding: "3px 6px", color: "#0F6E56" }}>{t("ev_deposit")}</td><td style={{ ...TR, color: "#0F6E56" }}>{formatPrice(ev.totals.deposit, cur)}</td></tr>
                    <tr><td style={{ padding: "3px 6px", color: "#8A5A0B" }}>{t("ev_balance")}</td><td style={{ ...TR, color: "#8A5A0B" }}>{formatPrice(ev.totals.balance, cur)}</td></tr>
                  </tbody>
                </table>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("ev_type")}</div>
                    <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                      <thead><tr><th style={TH}>{t("ev_type")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("revenue")}</th></tr></thead>
                      <tbody>{(ev.by_type || []).map((x) => (
                        <tr key={x.type}><td style={{ padding: "3px 6px" }}>{x.type}</td><td style={TR}>{x.count}</td><td style={TR}>{formatPrice(x.revenue, cur)}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("ev_status")}</div>
                    <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                      <thead><tr><th style={TH}>{t("ev_status")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("revenue")}</th></tr></thead>
                      <tbody>{(ev.by_status || []).map((x) => (
                        <tr key={x.status}><td style={{ padding: "3px 6px" }}>{x.status}</td><td style={TR}>{x.count}</td><td style={TR}>{formatPrice(x.revenue, cur)}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))
          )}
          {tab === "fb" && (
            fb == null ? <div className="muted" style={{ fontSize: 13 }}>...</div> :
            (!fb.count ? <div className="muted" style={{ fontSize: 13 }}>{t("none")}</div> : (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginBottom: 14 }}>
                  <div style={{ background: "#F7FAF9", borderRadius: 8, padding: "10px 12px" }}><div style={{ fontSize: 12, color: "#5f6b78" }}>{t("fb_overall")}</div><div style={{ fontSize: 22, fontWeight: 600 }}>{fb.avg_overall != null ? `${fb.avg_overall} ★` : "—"}</div></div>
                  <div style={{ background: "#F7FAF9", borderRadius: 8, padding: "10px 12px" }}><div style={{ fontSize: 12, color: "#5f6b78" }}>{t("fb_staff")}</div><div style={{ fontSize: 22, fontWeight: 600 }}>{fb.avg_staff != null ? `${fb.avg_staff} ★` : "—"}</div></div>
                  <div style={{ background: "#F7FAF9", borderRadius: 8, padding: "10px 12px" }}><div style={{ fontSize: 12, color: "#5f6b78" }}>{t("fb_positive")}</div><div style={{ fontSize: 22, fontWeight: 600, color: "#0F6E56" }}>{fb.positive_pct != null ? `${fb.positive_pct}%` : "—"}</div></div>
                  <div style={{ background: "#F7FAF9", borderRadius: 8, padding: "10px 12px" }}><div style={{ fontSize: 12, color: "#5f6b78" }}>{t("count")}</div><div style={{ fontSize: 22, fontWeight: 600 }}>{fb.count}</div></div>
                </div>
                <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                  <thead><tr><th style={TH}>{t("date")}</th><th style={TH}>{t("guest")}</th><th style={{ ...TH, ...TR }}>{t("fb_overall")}</th><th style={{ ...TH, ...TR }}>{t("fb_staff")}</th><th style={TH}>{t("fb_comment2")}</th></tr></thead>
                  <tbody>{(fb.recent || []).map((x, i) => (
                    <tr key={i}><td style={{ padding: "3px 6px" }}>{x.date ? x.date.slice(0, 10) : ""}</td><td>{x.guest_name}</td><td style={TR}>{x.overall} ★</td><td style={TR}>{x.staff} ★</td><td>{x.comment || "-"}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
